import { PhotoCheckConfig, PhotoCheckCriteria } from '@/types/photo-check.type';

type Node = { checklist: { photo_check?: any }; children: Node[] };

// Same limit as the backend; tested with 60 questions on one photo
export const PHOTO_CHECK_MAX_TASKS = 60;

function leavesUnder(node: Node): Node[] {
  return (node.children || []).flatMap(child =>
    child.children.length === 0 ? [child] : leavesUnder(child)
  );
}

/** What the creator set in the builder: how the photo should look, and example photos */
export function photoCriteria(node: Node | undefined): PhotoCheckCriteria {
  const config = node?.checklist?.photo_check || {};
  return {
    looks_like: config.looks_like?.trim() || undefined,
    references: config.references?.length ? config.references : undefined
  };
}

/**
 * Photo check is on for this checklist: the creator turned the toggle on. Checklists set up before the toggle
 * existed count as on when they already have a description or example photos.
 */
export function isPhotoCheckOn(node: Node | undefined) {
  const config = node?.checklist?.photo_check;
  if (!config) return false;
  if (typeof config.enabled === 'boolean') return config.enabled;
  return hasPhotoCriteria(node);
}

export function hasPhotoCriteria(node: Node | undefined) {
  const criteria = photoCriteria(node);
  return !!(criteria.looks_like || criteria.references);
}

/**
 * The tasks one photo checks: the node itself when it has no subtasks, otherwise every task without subtasks
 * under it, at any level. Jev checks each task's title, plus "what the photo should look like" when the
 * creator wrote one on that task. Example photos are shown to the person, not sent with each task.
 */
export function photoCheckTasks<T extends Node>(
  root: T | undefined
): { task: T; check: PhotoCheckConfig }[] {
  if (!root) return [];
  const tasks = root.children?.length ? leavesUnder(root) : [root];
  return tasks.slice(0, PHOTO_CHECK_MAX_TASKS).map(task => {
    const own = { ...(task.checklist?.photo_check || {}) };
    delete own.skip;
    delete own.media;
    delete own.references;
    if (!own.looks_like?.trim()) delete own.looks_like;
    return {
      task: task as T,
      check: { type: 'yesno', ...own } as PhotoCheckConfig
    };
  });
}
